import CheckIcon from "@mui/icons-material/Check";
import { Box, type SxProps, type Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type CSSProperties, type FC, isValidElement, type ReactNode } from "react";
import { ALPHA, FONT_SIZE, GRID_RADIUS, INK, MOTION, NEUTRAL, TOUCH_TARGET_MIN } from "@/theme/tokens";
import { MetaChip } from "@/ui/Badges/MetaChip";
import { GlyphButton } from "@/ui/Clickable/GlyphButton";
import { RoundedCheckboxBox, RoundedCheckboxState } from "@/ui/Clickable/RoundedCheckboxBox";
import { HighlightedText } from "@/ui/molecules/HighlightedText";
import { TruncatedText } from "@/ui/molecules/TruncatedText";
import { DropdownRowHint, dropdownRowHintRevealSx } from "./DropdownRowHint";
import type { DropdownPanelLook, DropdownRowAction, NormalizedItem } from "./types";
import type { HighlightSegment } from "./useDropdownSearch";

/** Row heights (px) used by the virtualized list — kept here so measurement and render agree. */
export const ROW_HEIGHT = { single: 40, double: 56, group: 34 } as const;

/**
 * On a coarse pointer the row IS the tap target, so it may not sit under `TOUCH_TARGET_MIN`. Given as a
 * whole separate height rather than a `min-height`: react-window is told each row's size up front, and a
 * CSS-only bump would leave measurement and render disagreeing — rows would overlap or leave gaps.
 * `double` already clears the minimum.
 */
export const ROW_HEIGHT_COARSE = { ...ROW_HEIGHT, single: TOUCH_TARGET_MIN, group: TOUCH_TARGET_MIN } as const;

/**
 * The `menu` look's rows (`panelLook="menu"`): the design system's menu item — 14px on 8px of padding — and its
 * section title, both a notch shorter than the field panel's. A coarse pointer keeps the touch heights.
 */
export const ROW_HEIGHT_MENU = { ...ROW_HEIGHT, single: 38, group: 32 } as const;

export type DropdownRowHeights = { single: number; double: number; group: number };

// DEFAULT (both helpers): `look` omitted is the field panel, which is what every caller measured before `menu`.
export const rowHeightsFor = (coarse: boolean, look: DropdownPanelLook = "standard"): DropdownRowHeights => {
	if (coarse) return ROW_HEIGHT_COARSE;
	return look === "menu" ? ROW_HEIGHT_MENU : ROW_HEIGHT;
};

export const rowHeightFor = (item: NormalizedItem<unknown>, coarse = false, look: DropdownPanelLook = "standard"): number => {
	const heights = rowHeightsFor(coarse, look);
	return item.secondaryLabel || item.avatar ? heights.double : heights.single;
};

/**
 * The dropdown's checkbox glyph — the shared `RoundedCheckboxBox`, decorative (the row/button conveys state), animated.
 * `square` is the `menu` look's box, as the design system draws a checkbox in a menu.
 */
export const DropdownCheckbox: FC<{ state: RoundedCheckboxState; square?: boolean }> = ({ state, square }) => (
	<RoundedCheckboxBox state={state} animated aria-hidden square={square} />
);

type Props<T> = {
	item: NormalizedItem<T>;
	multiple: boolean;
	hideCheckbox: boolean;
	selected: boolean;
	highlighted: boolean;
	highlightSegments: HighlightSegment[];
	onToggle: (id: string) => void;
	style?: CSSProperties;
	/** DOM id of the option, referenced by the listbox's `aria-activedescendant` (L10). */
	id?: string;
	dataTestId?: string;
	/** A child of a group header — indented so the group reads as a parent with nested options. */
	indent?: boolean;
	/** Coarse pointer: the row grows to `TOUCH_TARGET_MIN`. Passed in (not measured here) so this height
	 *  is the same one the virtualized list was told about. */
	coarse?: boolean;
	/** The panel's look (`SystemDropdown`'s `panelLook`); omitted it is `standard`. */
	look?: DropdownPanelLook;
	/** Runs a row's `rowAction` after closing the panel; omitted where the panel has none to run. */
	onRowAction?: (action: DropdownRowAction) => void;
};

/** Diameter (px) of the categorical color dot — shared with the closed-trigger display so it never resizes on select. */
export const DOT_SIZE = 9;
const DOT_BASE: SxProps<Theme> = { width: DOT_SIZE, height: DOT_SIZE, borderRadius: "50%", flex: "0 0 auto" };
// Single-line rows: dot centered on the name (row is alignItems:center). Two-line rows: align the dot to
// the first (name) line, not the two-line midpoint.
const DOT_TWO_LINE: SxProps<Theme> = { alignSelf: "flex-start", mt: "4px" };

// Render a trailing tag as a MetaChip (polish #2); pass through an already-built node otherwise.
const renderEnd = (endAdornment: ReactNode): ReactNode => {
	if (endAdornment === undefined || endAdornment === null) return null;
	if (typeof endAdornment === "string") return <MetaChip label={endAdornment} tone="neutral" />;
	return isValidElement(endAdornment) ? endAdornment : <MetaChip label={String(endAdornment)} tone="neutral" />;
};

export const DropdownRow = <T,>({
	item,
	multiple,
	hideCheckbox,
	selected,
	highlighted,
	highlightSegments,
	onToggle,
	style,
	id,
	dataTestId,
	indent,
	coarse,
	// DEFAULT: the field panel's row every caller had.
	look = "standard",
	onRowAction,
}: Props<T>) => {
	const showCheckbox = multiple && !hideCheckbox;
	const isMenu = look === "menu";
	const labelSx = {
		fontSize: FONT_SIZE.EMPHASIS,
		color: INK.PRIMARY,
		fontWeight: selected && !isMenu ? 600 : 400,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	} as const;
	const labelContent = highlightSegments.some((segment) => segment.match) ? <HighlightedText segments={highlightSegments} /> : item.label;
	const { rowAction } = item;
	return (
		<Box
			id={id}
			role="option"
			aria-selected={selected}
			aria-disabled={item.disabled || undefined}
			data-testid={dataTestId}
			style={style}
			onClick={item.disabled ? undefined : () => onToggle(item.id)}
			sx={(theme) => ({
				display: "flex",
				alignItems: "center",
				gap: 1.25,
				// Child rows sit under a group header — indent the leading edge so the nesting is visible. A menu's
				// section title is a heading, not a parent row, so its items keep the menu's own edge.
				paddingInlineStart: theme.spacing(isMenu ? 1.25 : indent ? 3.75 : 1.75),
				paddingInlineEnd: theme.spacing(isMenu ? 1.25 : 1.75),
				height: rowHeightFor(item, coarse, look),
				cursor: item.disabled ? "not-allowed" : "pointer",
				opacity: item.disabled ? 0.5 : 1,
				boxSizing: "border-box",
				transition: `background-color ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
				"@media (prefers-reduced-motion: reduce)": { transition: "none" },
				...(item.hint === undefined ? {} : dropdownRowHintRevealSx),
				...(isMenu
					? {
							// A menu marks its pick with the check, not a wash: the wash is the pointer's (and the keyboard's).
							borderRadius: GRID_RADIUS.CHIP,
							backgroundColor: highlighted ? alpha(theme.palette.primary.main, ALPHA.SELECTED_OVERLAY) : "transparent",
							"@media (hover: hover) and (pointer: fine)": { "&:hover": { backgroundColor: alpha(theme.palette.primary.main, ALPHA.SELECTED_OVERLAY) } },
						}
					: {
							backgroundColor: selected ? alpha(theme.palette.primary.main, 0.1) : highlighted ? NEUTRAL[50] : "transparent",
							"@media (hover: hover) and (pointer: fine)": {
								"&:hover": { backgroundColor: selected ? alpha(theme.palette.primary.main, 0.16) : NEUTRAL[50] },
							},
						}),
			})}
		>
			{item.startAdornment}
			{showCheckbox && <DropdownCheckbox state={selected ? RoundedCheckboxState.On : RoundedCheckboxState.Off} square={isMenu} />}
			{item.avatar}
			{item.icon}
			{item.color && <Box sx={[DOT_BASE, { backgroundColor: item.color }, ...(item.secondaryLabel ? [DOT_TWO_LINE] : [])]} />}
			<Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", flex: 1 }}>
				{item.fullLabelWhenTruncated ? (
					<TruncatedText text={item.labelText} sx={{ ...labelSx, display: "block" }}>
						{labelContent}
					</TruncatedText>
				) : (
					<Box component="span" sx={labelSx}>
						{labelContent}
					</Box>
				)}
				{item.secondaryLabel && (
					<Box component="span" sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
						{item.secondaryLabel}
					</Box>
				)}
			</Box>
			{renderEnd(item.endAdornment)}
			{item.hint !== undefined && <DropdownRowHint hint={item.hint} revealed={highlighted} />}
			{rowAction !== undefined && onRowAction !== undefined && (
				<GlyphButton
					icon={rowAction.icon}
					ariaLabel={rowAction.ariaLabel}
					dataTestId={rowAction.dataTestId}
					onClick={(event) => {
						// The command, not the row: the row stays unpicked.
						event.stopPropagation();
						onRowAction(rowAction);
					}}
				/>
			)}
			{/* Single-select trailing check on the selected row. */}
			{!multiple && selected && (
				<CheckIcon sx={(theme) => ({ fontSize: 16, color: isMenu ? theme.palette.primary.dark : theme.palette.primary.main, flex: "0 0 auto" })} />
			)}
		</Box>
	);
};
